// Minimap + lens panel (bottom left). The minimap paints every hex from the snapshot: water,
// unowned land (e.g. Antarctica) and ice from the land mask, owners in their colours with
// darker border hexes, occupation as diagonal stripes in the controller's colour, and the
// active lens in its legend colours. It flies the camera on click. Lens buttons come from
// world.lensIds(); the active lens shows its legend with data year and sources.
//
//   canvas pixel grid: 2 px per column, 1 px per row, odd rows shifted 1 px right,
//   so the 1024×337 bitmap maps hex-exactly and CSS scales it to the world aspect.
//
//   ▓▓▓▓▓▓▓▓      owner fill, border hexes darkened
//   ▓/▓▓/▓▓/      occupied: every third diagonal in the controller's colour
//   ░░░░░░░░      lens: legend colour per hex, owner borders kept for orientation

import { useEffect, useRef } from 'preact/hooks';
import { COLS, HEX_COUNT, ROWS, WORLD_H, WORLD_W, hexCol, hexIndex, hexRow, lonLatToWorld } from '../core/grid.ts';
import type { WorldService } from '../services/api.ts';
import type { LensResult, WorldSnapshot } from '../world/api.ts';
import { parseHex } from './color.ts';
import { useServices } from './context.ts';
import { footprintOf, landMask, type Footprint, type LandMask } from './extensions.ts';
import { Icon, lensIcon } from './icons.tsx';
import { legendModel, lensClaim } from './legend.ts';
import { ClaimLabel, Corners, SourceChip, useTip } from './parts.tsx';

const PX_PER_COL = 2;
const CANVAS_W = COLS * PX_PER_COL;
const CANVAS_H = ROWS;
const OCEAN = parseHex('#0d2b40');
const OCEAN_DEEP = parseHex('#081c2b');
const NEUTRAL_LAND = parseHex('#6e6a5e');
const ICE = parseHex('#cfd8dc');
const LENS_NO_DATA = parseHex('#2a3440');
const LAND_MIN = 128; // land / ice fraction (0..255) from which a hex counts as land / ice
const BORDER_DARKEN = 0.55;
const LAND_DIM = 0.86;
const LENS_BORDER_DARKEN = 0.8;
const STRIPE_EVERY = 3; // occupation: one diagonal in three carries the controller's colour
const RETICLE_R = 14;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function MinimapPanel() {
  const { state } = useServices();
  return (
    <div class="wc-minimap-wrap">
      {state.lensResult.value ? <LensLegend /> : null}
      <div class="wc-minimap wc-panel">
        <Corners />
        <LensBar />
        <Minimap />
      </div>
    </div>
  );
}

function Minimap() {
  const { state, world, actions } = useServices();
  const canvas = useRef<HTMLCanvasElement>(null);
  const snap = state.snapshot.value;
  const lens = state.lensResult.value;
  const cam = state.camera.value;
  const footprint = footprintOf(state);

  // Repaint only when owners, control or the lens actually changed (scrubbing dates mostly
  // keeps them all).
  const painted = useRef('');
  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx || !snap) {
      return;
    }
    const lensKey = lens && lens.values.length === HEX_COUNT ? `${lens.id}:${objectId(lens.values)}` : 'none';
    const key = `${snap.leader}:${checksum(snap.owner)}:${checksum(snap.controller)}:${lensKey}`;
    if (key === painted.current) {
      return;
    }
    painted.current = key;
    paint(ctx, snap, lens, world);
  }, [snap, lens]);

  const onClick = (e: MouseEvent) => {
    const el = canvas.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    const row = Math.min(ROWS - 1, Math.max(0, Math.floor(((e.clientY - r.top) / r.height) * ROWS)));
    const px = ((e.clientX - r.left) / r.width) * CANVAS_W - (row & 1);
    actions.flyTo(hexIndex(Math.floor(px / PX_PER_COL), row));
  };

  const reticle = cam ? hexToPct(cam.hex) : null;
  return (
    <div class="wc-minimap-map" style={{ aspectRatio: `${WORLD_W} / ${WORLD_H}` }}>
      <canvas ref={canvas} width={CANVAS_W} height={CANVAS_H} onClick={onClick} aria-label="Minimap: click to fly the camera" />
      {footprint ? <FootprintOverlay footprint={footprint} /> : null}
      {reticle && !footprint ? (
        <svg class="wc-minimap-reticle" style={{ left: `${reticle.x}%`, top: `${reticle.y}%` }} viewBox="-16 -16 32 32" aria-hidden="true">
          <circle r={RETICLE_R} fill="none" stroke="#f6dea0" stroke-width="1.6" />
          <path d="M0-16v7M0 9v7M-16 0h7M9 0h7" stroke="#f6dea0" stroke-width="1.6" />
        </svg>
      ) : null}
      <div class="wc-minimap-vignette" />
    </div>
  );
}

function hexToPct(hex: number): { x: number; y: number } {
  const col = hexCol(hex);
  const row = hexRow(hex);
  return { x: ((col * PX_PER_COL + (row & 1) + 1) / CANVAS_W) * 100, y: ((row + 0.5) / CANVAS_H) * 100 };
}

// Camera footprint: the ground under the four screen corners, as a trapezoid. Near the date
// line the corners straddle the seam, so the shape is drawn a second time one world over.
function FootprintOverlay({ footprint }: { footprint: Footprint }) {
  const pts = footprint.map(([lon, lat]) => {
    const w = lonLatToWorld(lon, lat);
    return { x: (w.x / WORLD_W) * 100, y: (w.z / WORLD_H) * 100 };
  });

  // Unwrap x so the polygon is continuous (no edge longer than half the world).
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    if (dx > 50) {
      pts[i].x -= 100;
    } else if (dx < -50) {
      pts[i].x += 100;
    }
  }
  const poly = (shift: number) => pts.map((p) => `${(p.x + shift).toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  const minX = Math.min(...pts.map((p) => p.x));
  const maxX = Math.max(...pts.map((p) => p.x));
  const shift = minX < 0 ? 100 : maxX > 100 ? -100 : 0;

  return (
    <svg class="wc-minimap-footprint" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={poly(0)} />
      {shift !== 0 ? <polygon points={poly(shift)} /> : null}
    </svg>
  );
}

// Stable small ids for typed arrays, so a new lens result repaints and the same one does not.
const ids = new WeakMap<object, number>();
let nextId = 1;

function objectId(o: object): number {
  let id = ids.get(o);
  if (id === undefined) {
    id = nextId;
    nextId += 1;
    ids.set(o, id);
  }
  return id;
}

// The land mask never changes; ask once per world service.
const masks = new WeakMap<WorldService, LandMask | null>();

function maskOf(world: WorldService): LandMask | null {
  if (!masks.has(world)) {
    masks.set(world, landMask(world));
  }
  return masks.get(world) ?? null;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

// Colour of a lens value: category → its legend entry; scalar → interpolated across the legend
// stops, evenly spaced over 0..1. NaN (no data) → null.
function lensColour(lens: LensResult, stops: Rgb[], v: number): Rgb | null {
  if (Number.isNaN(v) || stops.length === 0) {
    return null;
  }
  if (lens.kind === 'category') {
    return stops[Math.round(v)] ?? null;
  }
  const t = Math.min(1, Math.max(0, v)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(t));
  return stops.length === 1 ? stops[0] : mix(stops[i], stops[i + 1], t - i);
}

function paint(ctx: CanvasRenderingContext2D, snap: WorldSnapshot, lens: LensResult | null, world: WorldService): void {
  const img = ctx.createImageData(CANVAS_W, CANVAS_H);
  const data = img.data;
  const mask = maskOf(world);
  const lensOn = lens !== null && lens.values.length === HEX_COUNT;
  const stops = lensOn ? lens.legend.map((l) => parseHex(l.color)) : [];
  const palette = new Map<number, Rgb>();

  const colourOf = (actor: number): Rgb => {
    let c = palette.get(actor);
    if (!c) {
      const a = world.actor(actor);
      c = a ? parseHex(a.colors.primary) : NEUTRAL_LAND;
      palette.set(actor, c);
    }
    return c;
  };

  for (let i = 0; i < HEX_COUNT; i++) {
    const owner = snap.owner[i];
    const row = hexRow(i);
    const col = hexCol(i);
    // Without a land mask, only owned hexes are known to be land.
    const land = mask ? mask.land[i] >= LAND_MIN : owner !== 0;
    let c: Rgb;
    let k = 1;

    if (!land && owner === 0) {
      c = mix(OCEAN, OCEAN_DEEP, Math.abs(row / ROWS - 0.5) * 2);
    } else if (lensOn) {
      c = lensColour(lens, stops, lens.values[i]) ?? LENS_NO_DATA;
      k = owner !== 0 && isBorder(snap.owner, i) ? LENS_BORDER_DARKEN : 1;
    } else if (owner === 0) {
      c = mask?.ice && mask.ice[i] >= LAND_MIN ? ICE : NEUTRAL_LAND;
      k = LAND_DIM;
    } else {
      c = colourOf(owner);
      k = isBorder(snap.owner, i) ? BORDER_DARKEN : LAND_DIM;
      // Occupation: diagonal stripes in the controller's colour (1 px at the 2×1 pixel aspect).
      const ctrl = snap.controller[i];
      if (ctrl !== 0 && (col + row) % STRIPE_EVERY === 0) {
        c = colourOf(ctrl);
        k = 1;
      }
    }

    const x0 = col * PX_PER_COL + (row & 1);
    for (let dx = 0; dx < PX_PER_COL; dx++) {
      const x = (x0 + dx) % CANVAS_W;
      const p = (row * CANVAS_W + x) * 4;
      data[p] = c.r * k;
      data[p + 1] = c.g * k;
      data[p + 2] = c.b * k;
      data[p + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}

// Border = an owner change to the left/right in the row or straight above/below. On the
// minimap this 4-neighbourhood reads the same as true hex adjacency and needs no look-ups.
function isBorder(owner: Uint16Array, i: number): boolean {
  const col = i % COLS;
  const rowStart = i - col;
  const o = owner[i];
  const left = rowStart + ((col + COLS - 1) % COLS);
  const right = rowStart + ((col + 1) % COLS);
  if (owner[left] !== o || owner[right] !== o) {
    return true;
  }
  if (i >= COLS && owner[i - COLS] !== o) {
    return true;
  }
  return i + COLS < HEX_COUNT && owner[i + COLS] !== o;
}

// Order-sensitive 32-bit checksum of a per-hex array (FNV-style), to detect real changes.
function checksum(values: Uint16Array): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < values.length; i++) {
    h = Math.imul(h ^ values[i], 0x01000193);
  }
  return h >>> 0;
}

// ─── Lens bar + legend ───────────────────────────────────────────────────────────────────

function LensBar() {
  const { state, world, actions } = useServices();
  const active = state.lens.value;
  const mode = state.mode.value;
  return (
    <div class="wc-lensbar">
      <span class="wc-lensbar-label">Lenses</span>
      {world.lensIds().map((l) => (
        <LensButton key={l.id} id={l.id} name={l.name} active={active === l.id} />
      ))}
      <span class="wc-lensbar-gap" />
      <button
        type="button"
        class="wc-ibtn wc-lens-btn"
        aria-pressed={mode === 'strategic'}
        aria-label="Strategic view (=)"
        onClick={() => actions.setMode(mode === 'strategic' ? '3d' : 'strategic')}
      >
        <Icon name="map" size={16} />
      </button>
    </div>
  );
}

function LensButton({ id, name, active }: { id: string; name: string; active: boolean }) {
  const { actions } = useServices();
  const handlers = useTip(() => <div class="wc-tip-title">{name} lens</div>, 'above');
  return (
    <button
      type="button"
      class="wc-ibtn wc-lens-btn"
      aria-pressed={active}
      aria-label={`${name} lens`}
      onClick={() => actions.setLens(active ? null : id)}
      {...handlers}
    >
      <Icon name={lensIcon(id)} size={16} />
    </button>
  );
}

function LensLegend() {
  const { state, world, actions } = useServices();
  const lens = state.lensResult.value;
  if (!lens) {
    return null;
  }
  const name = world.lensIds().find((l) => l.id === lens.id)?.name ?? lens.id;
  const model = legendModel(lens);
  const claim = lensClaim(lens.id);
  return (
    <div class="wc-legend wc-panel">
      <div class="wc-legend-head">
        <Icon name={lensIcon(lens.id)} size={16} />
        <span class="wc-legend-title">{name}</span>
        <button type="button" class="wc-legend-x" aria-label="Close lens" onClick={() => actions.setLens(null)}>
          <Icon name="close" size={13} />
        </button>
      </div>
      {model.shape === 'ramp' ? (
        <div class="wc-legend-ramp">
          <div class="wc-legend-bar" style={{ background: model.gradient }} />
          <div class="wc-legend-ticks">
            {model.ticks.map((t) => (
              <span key={t.key} class={`wc-legend-tick wc-legend-tick--${t.align}`} style={{ left: `${t.at}%` }}>
                {t.label}
              </span>
            ))}
          </div>
          {model.unit ? <div class="wc-legend-unit">{model.unit}</div> : null}
        </div>
      ) : (
        <ul class="wc-legend-list">
          {model.rows.map((r) => (
            <li key={r.key}>
              <span class="wc-swatch" style={{ background: r.color }} />
              {r.label}
            </li>
          ))}
        </ul>
      )}
      <div class="wc-legend-foot">
        {claim.kind ? <ClaimLabel kind={claim.kind} /> : null}
        {claim.note ? <span class="wc-year">{claim.note}</span> : null}
        {lens.sources.length > 0 ? <span class="wc-year">{lens.year !== undefined ? 'Data year' : 'Sources'}</span> : null}
        {lens.sources.length > 0 ? <SourceChip ids={lens.sources} year={lens.year} /> : null}
      </div>
    </div>
  );
}
